<script lang="ts">
  import { auth, profileModalOpen, closeProfileModal, saveProfile, changeOwnPassword } from "../lib/stores";
  import { avatarUrl } from "../lib/pocketbase";

  let name = "";
  let avatarFile: File | null = null;
  let avatarPreview: string | null = null;
  let removeAvatar = false;
  let savingProfile = false;
  let profileError = "";

  let oldPassword = "", newPassword = "", confirmPassword = "";
  let savingPassword = false;
  let passwordError = "";

  let lastOpenedFor: string | null = null;
  $: if ($profileModalOpen && $auth && $auth.record.id !== lastOpenedFor) {
    lastOpenedFor = $auth.record.id;
    name = $auth.record.name ?? "";
    avatarFile = null;
    avatarPreview = null;
    removeAvatar = false;
    profileError = "";
    oldPassword = ""; newPassword = ""; confirmPassword = "";
    passwordError = "";
  }
  $: if (!$profileModalOpen) lastOpenedFor = null;

  $: currentAvatarUrl = $auth ? avatarUrl($auth.record) : null;
  $: displayAvatar = avatarPreview ?? (removeAvatar ? null : currentAvatarUrl);

  function onAvatarPick(e: Event) {
    const file = (e.currentTarget as HTMLInputElement).files?.[0] ?? null;
    if (!file) return;
    if (!file.type.startsWith("image/")) { profileError = "Pick an image file."; return; }
    if (file.size > 5 * 1024 * 1024) { profileError = "Image must be under 5MB."; return; }
    profileError = "";
    removeAvatar = false;
    avatarFile = file;
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    avatarPreview = URL.createObjectURL(file);
  }

  function clearAvatar() {
    avatarFile = null;
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    avatarPreview = null;
    removeAvatar = true;
  }

  async function submitProfile() {
    profileError = "";
    savingProfile = true;
    try {
      await saveProfile({ name, avatarFile, removeAvatar });
      avatarFile = null;
      removeAvatar = false;
      if (avatarPreview) { URL.revokeObjectURL(avatarPreview); avatarPreview = null; }
    } catch (e) {
      profileError = e instanceof Error ? e.message : "Couldn't save profile.";
    } finally {
      savingProfile = false;
    }
  }

  async function submitPassword() {
    passwordError = "";
    if (!oldPassword || !newPassword) { passwordError = "Fill in both password fields."; return; }
    if (newPassword.length < 8) { passwordError = "New password needs at least 8 characters."; return; }
    if (newPassword !== confirmPassword) { passwordError = "New passwords don't match."; return; }
    savingPassword = true;
    try {
      await changeOwnPassword(oldPassword, newPassword);
      oldPassword = ""; newPassword = ""; confirmPassword = "";
    } catch (e) {
      passwordError = e instanceof Error ? e.message : "Couldn't change password.";
    } finally {
      savingPassword = false;
    }
  }

  function onOverlayClick(e: MouseEvent) {
    if (e.target === e.currentTarget) closeProfileModal();
  }
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") closeProfileModal();
  }
</script>

<svelte:window on:keydown={onKeydown} />

{#if $profileModalOpen && $auth}
  <div class="modal-overlay" on:click={onOverlayClick} role="presentation">
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="profileModalTitle">
      <div class="modal-head">
        <div class="modal-title" id="profileModalTitle">My account</div>
        <button class="modal-close" type="button" aria-label="Close" on:click={closeProfileModal}>✕</button>
      </div>
      <div class="modal-body">
        <div class="modal-readonly">Email</div>
        <div class="modal-readonly-value">{$auth.record.email}</div>

        <div class="avatar-row">
          {#if displayAvatar}
            <img class="avatar-preview" src={displayAvatar} alt="Avatar preview" />
          {:else}
            <div class="avatar-preview avatar-placeholder">{(name || $auth.record.email).charAt(0).toUpperCase()}</div>
          {/if}
          <div class="avatar-actions">
            <label class="btn" style="cursor:pointer;">
              Choose image
              <input type="file" accept="image/*" style="display:none" on:change={onAvatarPick} />
            </label>
            {#if displayAvatar}
              <button class="btn" type="button" on:click={clearAvatar}>Remove</button>
            {/if}
          </div>
        </div>

        <label>Name
          <input type="text" bind:value={name} placeholder="Your name" />
        </label>

        {#if profileError}<div class="modal-hint" style="color:var(--status-critical);">{profileError}</div>{/if}

        <div class="modal-foot" style="padding:0;border-top:none;">
          <div class="spacer"></div>
          <button class="btn primary" type="button" disabled={savingProfile} on:click={submitProfile}>
            {savingProfile ? "Saving…" : "Save profile"}
          </button>
        </div>

        <hr style="border:none;border-top:1px solid var(--border);margin:4px 0;" />

        <div class="modal-readonly">Change password</div>
        <label>Current password
          <input type="password" bind:value={oldPassword} autocomplete="current-password" />
        </label>
        <div class="modal-row">
          <label>New password
            <input type="password" bind:value={newPassword} autocomplete="new-password" />
          </label>
          <label>Confirm new password
            <input type="password" bind:value={confirmPassword} autocomplete="new-password" />
          </label>
        </div>

        {#if passwordError}<div class="modal-hint" style="color:var(--status-critical);">{passwordError}</div>{/if}

        <div class="modal-foot" style="padding:0;border-top:none;">
          <div class="spacer"></div>
          <button class="btn" type="button" disabled={savingPassword} on:click={submitPassword}>
            {savingPassword ? "Changing…" : "Change password"}
          </button>
        </div>
      </div>
      <div class="modal-foot">
        <div class="spacer"></div>
        <button class="btn" type="button" on:click={closeProfileModal}>Close</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .avatar-row { display:flex; align-items:center; gap:12px; }
  .avatar-preview {
    width:56px; height:56px; border-radius:50%; object-fit:cover; flex-shrink:0;
    background:var(--surface-2); border:1px solid var(--border);
  }
  .avatar-placeholder {
    display:flex; align-items:center; justify-content:center;
    font-size:20px; font-weight:700; color:var(--text-secondary);
  }
  .avatar-actions { display:flex; flex-direction:column; gap:6px; }
</style>
